import { useEffect, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, IconName, Modal } from '@orbita-ui/core';

import { PageTitle, PBody } from 'shared/components/Fonts';
import { Card } from 'shared/components';
import { ListContainer, ListItem } from 'shared/components/List';
import { CustomBreadcrumb } from 'shared/components/CustomBreadcrumb';
import { BaseModal, useModal } from 'shared/modal';
import { useDataFetcher } from 'shared/http';

import { useCoreService } from 'core/services/useCoreService';
import { CentroCosto } from 'core/centrocosto/models/CentroCosto';
import {
  CostCentreForm,
  CostCentreFormData,
} from 'core/centrocosto/components/CostCentreForm/CostCentreForm';

import { COST_CENTRE_EDIT_PATH, COST_CENTRE_LIST_PATH } from 'core/centrocosto/routes';

import { messages } from './Message';
import { CardsTableWithPagination } from 'core/tarjeta/components/CardsTableWithPagination/CardsTableWithPagination';
import { CostCentreTableWithPagination } from 'core/centrocosto/components/CostCentreTableWithPagination/CostCentreTableWithPagination';
import { CostCentreMovePageSkeleton } from './CostCentreMovePageSkeleton';
import { useNotifications } from 'shared/notifications';
import { MOVE_COST_CENTRE_ERROR_MESSAGES } from 'core/centrocosto/constants/MoveCostCentreMessages';

export function CostCentreMovePage() {
  const breadcrumbData: {
    icon: IconName;
    items: string[];
    lastItem: string;
  } = {
    icon: 'BankIcon',
    items: ['Organización'],
    lastItem: 'Mover centro de costos',
  };

  const navigate = useNavigate();
  const { id } = useParams();
  const coreService = useCoreService();

  const [modalMessage, setModalMessage] = useState('');

  const { showModal, openModal, closeModal } = useModal(false);
  const { addNotification } = useNotifications();

  const [centroCostoResponse, setFetchCentroCosto] = useDataFetcher<CentroCosto>();

  useEffect(() => {
    setFetchCentroCosto(() => coreService.getCostCentre(id));
  }, [coreService, id, setFetchCentroCosto]);

  const onSubmit = async (data: CostCentreFormData) => {
    try {
      if (!centroCostoResponse.data || !data.centroCosto.value) return;

      await coreService.moveCostCentre(Number(id), {
        centroCostoPadreId: data.centroCosto.value.id,
      });
      addNotification('El centro de costos fue movido con éxito', 'success');
      navigate(COST_CENTRE_LIST_PATH);
    } catch (error: any) {
      const msg = MOVE_COST_CENTRE_ERROR_MESSAGES[error?.apiError?.message];
      setModalMessage(msg || MOVE_COST_CENTRE_ERROR_MESSAGES.default);
      openModal();
    }
  };

  return (
    <Container fluid>
      <CustomBreadcrumb {...breadcrumbData} />

      {/* Header */}
      <Row className="mb-3 d-flex justify-content-between align-items-center">
        <Col>
          <PageTitle>Mover Centro de Costos</PageTitle>
        </Col>
        <Col className="text-end">
          <Button
            color="primary"
            icon="PenIcon"
            onClick={() =>
              navigate(
                COST_CENTRE_EDIT_PATH.replace(
                  ':id',
                  String(centroCostoResponse.data?.id),
                ),
              )
            }
          >
            Modificar Centro de Costos
          </Button>
        </Col>
      </Row>

      {centroCostoResponse.isLoading ? (
        <CostCentreMovePageSkeleton />
      ) : (
        <>
          {/* Datos del centro de costos */}
          <Row className="mb-3">
            <Col>
              <Card>
                <Card.Title>Datos del Centro de Costos</Card.Title>
                <Card.Body className="p-3">
                  <Row>
                    <Col md={6}>
                      <>
                        <strong>Denominación:</strong>{' '}
                        {centroCostoResponse.data?.nombre ?? '-'}
                      </>
                    </Col>
                    <Col md={6}>
                      <>
                        <strong>Cuenta:</strong>{' '}
                        {centroCostoResponse.data?.numeroCuenta ?? '-'}
                      </>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Reglas */}
          <Row className="mb-3">
            <Col>
              <Card variant="info">
                <Card.Body $spacing>
                  <PBody className="fw-semibold">
                    Antes de mover el centro de costos, tenga en cuenta lo siguiente:
                  </PBody>
                  <ListContainer>
                    {messages.rules.map((rule) => (
                      <ListItem key={rule}>{rule}</ListItem>
                    ))}
                  </ListContainer>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Tarjetas */}
          <Row className="mb-3">
            <Col>
              <Card>
                <Card.Title>Tarjetas con Saldo</Card.Title>
                <Card.Body>
                  <CardsTableWithPagination
                    searchParams={{
                      numeroCuentaOrigen: centroCostoResponse.data?.numeroCuenta,
                      conSaldo: true,
                      cuentasHuerfanas: false,
                    }}
                    buttons={false}
                    noResultsMessage="No se encontraron tarjetas con saldo que dependan de éste centro de costos"
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Centros de costos */}
          <Row className="mb-3">
            <Col>
              <Card>
                <Card.Title>Centros de costos con Saldo</Card.Title>
                <Card.Body>
                  <CostCentreTableWithPagination
                    searchParams={{
                      numeroCuentaOrigen: centroCostoResponse.data?.numeroCuenta,
                      conSaldo: true,
                      cuentasHuerfanas: false,
                    }}
                    showButtons={false}
                    noResultsMessage="No se encontraron centros de costos con saldo que dependan de éste centro de costos"
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Modal de error */}
          <Modal show={showModal} onClose={closeModal}>
            <BaseModal
              title="Mover Centro de Costos"
              body={<p>{modalMessage}</p>}
              footer={
                <Button
                  className="bg-secondary"
                  color="inverted"
                  variant="ghost"
                  onClick={closeModal}
                >
                  Aceptar
                </Button>
              }
            />
          </Modal>

          {/* Formulario */}
          <Row>
            <CostCentreForm
              onSubmit={onSubmit}
              centroCosto={centroCostoResponse.data}
              title="Asociar a nuevo centro de costos"
              submitLabel="Mover"
              fields={{ centroCosto: true, cuenta: false, denominacion: false }}
            />
          </Row>
        </>
      )}
    </Container>
  );
}
